"use client";

import { forwardRef, ButtonHTMLAttributes } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import Loader from "./Loader";
import type { ButtonProps } from "@/types";

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      fullWidth = false,
      asLink = false,
      href,
      loading = false,
      leftIcon,
      rightIcon,
      className = "",
      customBgColor,
      customTextColor,
      customHoverColor,
      children,
      disabled,
      ...props
    },
    ref
  ) => {

    const baseClasses = "inline-flex items-center justify-center font-bold rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed hover:cursor-pointer";

    const getVariantClasses = () => {
      if (customBgColor || customTextColor) {
        return "focus:ring-opacity-50";
      }

      switch (variant) {
        case "primary":
          return "bg-brand-red text-white hover:opacity-90 focus:ring-brand-red";
        case "secondary":
          return "bg-brand-blue text-white hover:opacity-90 focus:ring-brand-blue";
        case "outline":
          return "border-2 border-brand-blue text-brand-blue bg-transparent hover:bg-brand-blue hover:text-white focus:ring-brand-blue";
        case "ghost":
          return "text-brand-blue bg-transparent hover:bg-gray-100 focus:outline-none focus:ring-0";
        case "danger":
          return "bg-red-600 text-white hover:bg-red-700 focus:ring-red-600";
        default:
          return "bg-brand-red text-white hover:opacity-90 focus:ring-brand-red";
      }
    };

    const getCustomStyles = () => {
      const styles: React.CSSProperties = {};
      
      if (customBgColor) {
        styles.backgroundColor = customBgColor;
      }
      
      if (customTextColor) {
        styles.color = customTextColor;
      }
      
      return styles;
    };


    const getSizeClasses = () => {
      switch (size) {
        case "sm":
          return "py-2 px-4 text-sm";
        case "md":
          return "py-3 px-6 text-base";
        case "lg":
          return "py-3 px-10 md:px-14 text-base md:text-lg lg:text-xl";
        case "xl":
          return "py-4 px-12 md:px-16 text-lg md:text-xl lg:text-2xl";
        default:
          return "py-3 px-6 text-base";
      }
    };

    const getWidthClasses = () => {
      return fullWidth ? "w-full" : "";
    };

    const buttonClasses = cn(
      baseClasses,
      getVariantClasses(),
      getSizeClasses(),
      getWidthClasses(),
      className
    );

    const getLoaderColor = () => {
      switch (variant) {
        case "primary":
        case "danger":
          return "white";
        case "secondary":
          return "white";
        case "outline":
        case "ghost":
          return "brand-blue";
        default:
          return "white";
      }
    };

    const getLoaderSize = () => {
      switch (size) {
        case "sm":
          return "sm";
        case "md":
          return "sm";
        case "lg":
          return "md";
        case "xl":
          return "md";
        default:
          return "sm";
      }
    };

    const buttonContent = (
      <>
        {loading && (
          <Loader 
            size={getLoaderSize()} 
            color={getLoaderColor()} 
            className="-ml-1 mr-2"
          />
        )}
        {!loading && leftIcon && <span className="mr-2">{leftIcon}</span>}
        {children}
        {!loading && rightIcon && <span className="ml-2">{rightIcon}</span>}
      </>
    );

    if (asLink && href) {
      return (
        <Link href={href} className={buttonClasses}>
          {buttonContent}
        </Link>
      );
    }

    return (
      <button
        ref={ref}
        className={buttonClasses}
        style={getCustomStyles()}
        disabled={disabled || loading}
        {...props}
      >
        {buttonContent}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
